import DOMPurify from "dompurify";

/**
 * Zengin metin (ReactQuill) yardımcıları.
 *
 * ## ⚠️ UPSTREAM'DEN BİLİNÇLİ SAPMA — DEPOLANMIŞ XSS
 *
 * Upstream (`366a5d0` / `09df543`) bu dosyayı **sanitizasyon olmadan** yazdı ve
 * `StatusTab`'de notları doğrudan `dangerouslySetInnerHTML` ile bastı:
 *
 *     git grep -n "dompurify\|sanitize" origin/dev -- package.json src/   → 0 sonuç
 *
 * Bu istismar edilebilir bir açıktı:
 *
 *  - **Kaynak:** `StateInfo.Statement`. Durum kaydı modalındaki "Kaynak Kodu"
 *    geçişi kullanıcıya **serbest HTML** yazdırıyor; Quill'in kendi çıktısıyla
 *    sınırlı değil.
 *  - **Sink:** `StatusTab` tablosundaki `dangerouslySetInnerHTML={{ __html: notes }}`.
 *  - **Etki:** React `innerHTML` üzerinden `<script>` ÇALIŞTIRMAZ, ama
 *    `<img src=x onerror=...>` ve `<svg onload=...>` **çalışır**. Yük, notu
 *    YAZAN kullanıcının değil, aday detayını **AÇAN** kullanıcının oturumunda
 *    tetiklenir (İK uzmanı A yazar, İK uzmanı B okur) — yani ayrıcalık sınırını
 *    aşan depolanmış XSS.
 *
 * Bu yüzden burada `dompurify` ile bir **izin listesi (allow-list)** uygulanıyor.
 * Etiket soymak (regex) sanitizasyon DEĞİLDİR; `previewHtmlAsText` gibi
 * fonksiyonlar yalnızca düz metin önizlemesi üretir, güvenlik sınırı değildir.
 *
 * İzin listesi bilinçli olarak DAR: Quill'in "snow" araç çubuğunun ürettiği
 * biçimlendirmeyi kapsar, fazlasını kapsamaz. `onerror` / `onload` gibi olay
 * öznitelikleri, `style`, `srcset`, `<img>`, `<svg>`, `<iframe>` ve
 * `javascript:` şemalı bağlantılar geçmez.
 *
 * Bkz. `docs/repo-migration/NOT_PORTED.md` §2.2.
 */

/** ReactQuill "snow" araç çubuğunun ürettiği etiketler. */
const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "ul",
  "ol",
  "li",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "a",
  "span",
  "div",
  "pre",
  "code",
  "sub",
  "sup",
];

/**
 * Yalnızca bağlantı hedefi ve Quill'in liste girinti sınıfı.
 *
 * `style` BİLEREK YOK: `style` üzerinden `background-image: url(javascript:...)`
 * ve eski tarayıcılarda `expression()` gibi vektörler açılır; ayrıca kiracının
 * tema renklerini ezerek okunamaz notlar üretebilir.
 */
const ALLOWED_ATTR = ["href", "target", "rel", "class"];

/**
 * Zengin metni güvenli hâle getirir.
 *
 * `dangerouslySetInnerHTML` kullanan HER yerde bu fonksiyondan geçilmelidir.
 * Sunucu tarafında ikinci bir savunma yoktur (BE `Statement` alanına serbest
 * metin kabul ediyor), yani bu tek savunma hattıdır.
 */
export function sanitizeRichText(html?: string | null): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // `javascript:` / `data:` şemalı href'leri düşürür.
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
}

export function isHtmlContentEmpty(html?: string | null): boolean {
  if (!html) return true;
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .trim();
  return text === "";
}

export function looksLikeHtml(value?: string | null): boolean {
  if (!value) return false;
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

/**
 * HTML'i düz metne indirir.
 *
 * NOT: bu bir GÜVENLİK sınırı değildir — çıktısı metin olarak render edilmek
 * üzere tasarlandı (`<Typography>{...}</Typography>`). HTML olarak basılacak
 * içerik için `sanitizeRichText` kullanın.
 */
export function stripHtmlToText(html?: string | null): string {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();
}

export function previewHtmlAsText(html?: string | null, maxLen = 500): string {
  const text = stripHtmlToText(html);
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}...`;
}
